import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobTemplatesSettings = () => {
  const [items, setItems] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited or {} for new

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/admin/job-templates?include_inactive=true`, { withCredentials: true }),
        axios.get(`${API}/service-types`, { withCredentials: true }),
      ]);
      setItems(t.data);
      setServiceTypes(s.data);
    } catch (err) { console.error(err); toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: '', service_type: '', dispatcher_notes: '', checklist: [''], active: true });
  const startEdit = (t) => setEditing({ ...t, checklist: t.checklist?.length ? t.checklist : [''] });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Template name required'); return; }
    const payload = {
      name: editing.name.trim(),
      service_type: editing.service_type || '',
      dispatcher_notes: editing.dispatcher_notes || '',
      checklist: (editing.checklist || []).map((s) => s.trim()).filter(Boolean),
      active: editing.active !== false,
      order: editing.order || items.length,
      followup_days: Number.parseInt(editing.followup_days || 0, 10) || 0,
      auto_create_followup: !!editing.auto_create_followup,
    };
    try {
      if (editing.id) {
        await axios.patch(`${API}/admin/job-templates/${editing.id}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/job-templates`, payload, { withCredentials: true });
        toast.success('Created');
      }
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const disable = async (id) => {
    try {
      await axios.delete(`${API}/admin/job-templates/${id}`, { withCredentials: true });
      toast.success('Disabled'); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="job-templates-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Job Templates</h2>
          <p className="text-[#A3A3A3] text-sm">Save common job setups (Water Heater, Disposal, Drain Cleaning…) so dispatch can one-click pre-fill new shifts.</p>
        </div>
        <Button data-testid="jt-new" onClick={startNew} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" />New Template</Button>
      </div>

      {editing && (
        <div className="bg-[#141414] border border-[#D4AF37]/40 rounded-md p-5 space-y-4" data-testid="jt-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Template Name</label>
              <Input data-testid="jt-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Water Heater Replacement" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Default Service Type</label>
              <Select value={editing.service_type || ''} onValueChange={(v) => setEditing({ ...editing, service_type: v })}>
                <SelectTrigger data-testid="jt-service-type" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="Pick service type" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] max-h-72 overflow-y-auto">
                  {serviceTypes.map((s) => <SelectItem key={s.id} value={s.name} className="text-white">{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Default Dispatcher Notes</label>
            <Textarea data-testid="jt-dispatcher-notes" rows={2} value={editing.dispatcher_notes || ''} onChange={(e) => setEditing({ ...editing, dispatcher_notes: e.target.value })} placeholder="Pre-fill for internal notes — gate codes, common warnings, etc." className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
          </div>
          <div>
            <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Checklist Items (one per line)</label>
            <Textarea
              data-testid="jt-checklist"
              rows={5}
              value={(editing.checklist || []).join('\n')}
              onChange={(e) => setEditing({ ...editing, checklist: e.target.value.split('\n') })}
              placeholder="Verify shut-off valve\nPhoto of old unit\nConfirm warranty"
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: v })} className="data-[state=checked]:bg-green-600" />
            <span className="text-white text-sm">Active</span>
          </div>
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-md p-3 space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold">Recurring Follow-up</p>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-3 items-end">
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Days Until Next Visit</label>
                <Input
                  data-testid="jt-followup-days"
                  type="number"
                  min="0"
                  step="1"
                  value={editing.followup_days || 0}
                  onChange={(e) => setEditing({ ...editing, followup_days: Number(e.target.value) })}
                  placeholder="365"
                  className="bg-[#141414] border-[#262626] text-white mt-1"
                />
              </div>
              <label className="flex items-center gap-2 text-white text-sm">
                <Switch data-testid="jt-auto-followup" checked={!!editing.auto_create_followup} onCheckedChange={(v) => setEditing({ ...editing, auto_create_followup: v })} className="data-[state=checked]:bg-[#D4AF37]" />
                Auto-create draft appointment on completion
              </label>
            </div>
            <p className="text-[10px] text-[#A3A3A3]">When a tech completes a job using this template, NexFlow will drop a <span className="text-white">draft</span> appointment on the dispatch board this many days later. Manager toggles it off per customer if needed.</p>
          </div>
          <div className="flex gap-2 pt-2 border-t border-[#262626]">
            <Button data-testid="jt-save" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Save className="w-4 h-4 mr-1" />Save</Button>
            <Button onClick={cancel} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><X className="w-4 h-4 mr-1" />Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-[#141414] border border-[#262626] rounded-md divide-y divide-[#262626]" data-testid="jt-list">
        {items.length === 0 && <div className="p-6 text-center text-[#A3A3A3]">No templates yet. Create your first one above.</div>}
        {items.map((t) => (
          <div key={t.id} className={`p-4 flex items-center justify-between gap-3 ${!t.active ? 'opacity-50' : ''}`} data-testid={`jt-row-${t.id}`}>
            <button onClick={() => startEdit(t)} className="text-left flex-1" data-testid={`jt-edit-${t.id}`}>
              <p className="text-white font-medium">{t.name}</p>
              <p className="text-[#A3A3A3] text-xs">
                {t.service_type ? `${t.service_type} · ` : ''}
                {(t.checklist?.length || 0)} checklist item{(t.checklist?.length || 0) === 1 ? '' : 's'}
                {t.auto_create_followup ? ` · Auto-followup in ${t.followup_days}d` : ''}
                {!t.active && ' · Disabled'}
              </p>
            </button>
            <Button data-testid={`jt-delete-${t.id}`} onClick={() => disable(t.id)} variant="outline" size="sm" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobTemplatesSettings;
